import { getQueryParams, removeQueryParam } from '@wk/wk-uni-lib';
import dayjs from 'dayjs';
import cookie from 'js-cookie';
import { redirect } from 'next/navigation';
import { useEffect } from 'react';

import { refreshTokenV2 } from '@/api/authorized';
import {
  LAST_REFRESH_TIME,
  NEED_REFRESH_TOKEN_DAY,
  NO_AUTH_PATHS,
  MOBILE_ONLY_NO_AUTH_PATHS,
  REFRESH_TOKEN,
  TOKEN,
} from '@/constants';
import CacheStorage from '@/utils/cache-storage';
import { goWebSite } from '@/utils/go-web-site';
import {
  isTourist,
  removeRealToken,
  removeTokenInfo,
  setRealToken,
  setTokenInfo,
} from '@/utils/helpers';
import { isRealMobileUserAgent } from '@/utils/ua';
import { getCurrentUrlInfoInClient } from '@/utils/url';

import { useLanguage } from './use-language';

const useAuth = (headerToken?: string) => {
  const { currentLanguage } = useLanguage();

  useEffect(() => {
    const { pathname } = getCurrentUrlInfoInClient();

    // 基于 User-Agent 的真实移动端判断（与服务端一致）
    const ua =
      typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    const isRealMobileDevice = isRealMobileUserAgent(ua);

    /* 如果路径不需要登录权限（全端），则不跳回登录页 */
    if (NO_AUTH_PATHS.includes(pathname)) {
      return;
    }

    const tourist = isTourist();
    const { token: urlToken, noAuth } = getQueryParams();

    /* 移动端免登录路径特殊处理 */
    const isMobileNoAuthPath =
      MOBILE_ONLY_NO_AUTH_PATHS.includes(pathname) && isRealMobileDevice;

    if (isMobileNoAuthPath) {
      // 检查是否有任何来源的 token
      const hasUrlOrHeaderToken = !!(urlToken || headerToken);
      const localToken = CacheStorage.getItem<string>(TOKEN);

      // 如果没有任何 token，则免登录直接返回
      if (!hasUrlOrHeaderToken && !localToken) {
        return;
      }
      // 如果有 token，继续执行后续逻辑（token 刷新等）
    }

    if (noAuth) {
      redirect(`/${currentLanguage}/401`);
    }

    if (tourist) {
      // 临时存储 sign，手机号校验后清除
      removeRealToken();
    }

    if (urlToken || headerToken) {
      const realToken = urlToken ? urlToken.toString() : headerToken;
      if (!realToken) {
        return;
      }
      if (
        [':3000/', '-3000.'].some((val: string) =>
          window.location.href.includes(val),
        )
      ) {
        cookie.set(TOKEN, realToken, { expires: 30 });
      } else {
        setRealToken(realToken);
      }
      const newHref = removeQueryParam(window.location.href, 'token');
      window.history.replaceState({}, '', newHref);
    }

    const token = CacheStorage.getItem<string>(TOKEN);
    const lastRefreshTime = CacheStorage.getItem<string>(LAST_REFRESH_TIME);

    if (!tourist && !token) {
      goWebSite();
      return;
    }

    if (
      token &&
      CacheStorage.getItem(REFRESH_TOKEN) &&
      (!lastRefreshTime ||
        dayjs().add(NEED_REFRESH_TOKEN_DAY, 'day').unix() > +lastRefreshTime)
    ) {
      refreshTokenV2({
        refreshToken: CacheStorage.getItem(REFRESH_TOKEN),
      })
        .then(res => {
          const { content } = res || {};
          const { token: newToken, refreshToken: newRefreshToken } =
            content || {};
          if (newToken && newRefreshToken) {
            setTokenInfo(newToken, newRefreshToken);
          } else {
            removeTokenInfo(null, true);
          }
        })
        .catch(e => {
          console.error(e, 'useAuth catch');
          removeTokenInfo(null, true);
        });
    }
  }, []);
};

export default useAuth;
